import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SuperAdminNav from '../components/SuperAdminNav';

const LOGO = '/dailyreps3.png';

export default function StaffAccount() {
  const { user, teams, apiFetch, logout } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Club name state (club_admin only)
  const [clubName, setClubName] = useState('');
  const [clubLoading, setClubLoading] = useState(false);
  const [clubSaving, setClubSaving] = useState(false);
  const [clubError, setClubError] = useState('');
  const [clubSuccess, setClubSuccess] = useState('');

  // Team name state
  const [selectedTeamId, setSelectedTeamId] = useState(teams?.[0]?.id || '');
  const [teamName, setTeamName] = useState('');
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSuccess, setTeamSuccess] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Load club name for club_admin
  useEffect(() => {
    if (user?.role === 'club_admin') {
      setClubLoading(true);
      apiFetch('/api/club/dashboard')
        .then(data => setClubName(data.club?.name || ''))
        .catch(() => {})
        .finally(() => setClubLoading(false));
    }
  }, [user?.role, apiFetch]);

  // Set team name when team selection changes
  useEffect(() => {
    if (selectedTeamId && teams?.length > 0) {
      const team = teams.find(t => t.id === selectedTeamId);
      setTeamName(team?.name || '');
      setTeamError('');
      setTeamSuccess('');
    }
  }, [selectedTeamId, teams]);

  const backPath = user?.role === 'club_admin' ? '/club' : user?.role === 'super_admin' ? '/super' : '/admin';

  // Profile save
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError(''); setProfileSuccess(''); setProfileSaving(true);
    try {
      await apiFetch('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email }),
      });
      setProfileSuccess('Profile updated.');
    } catch (err) { setProfileError(err.message); }
    finally { setProfileSaving(false); }
  };

  // Club name save
  const handleSaveClubName = async (e) => {
    e.preventDefault();
    setClubError(''); setClubSuccess(''); setClubSaving(true);
    try {
      await apiFetch('/api/club/name', {
        method: 'PUT',
        body: JSON.stringify({ name: clubName }),
      });
      setClubSuccess('Club name updated.');
    } catch (err) { setClubError(err.message); }
    finally { setClubSaving(false); }
  };

  // Team name save
  const handleSaveTeamName = async (e) => {
    e.preventDefault();
    setTeamError(''); setTeamSuccess(''); setTeamSaving(true);
    try {
      await apiFetch('/api/admin/team/name', {
        method: 'PUT',
        headers: { 'x-team-id': selectedTeamId },
        body: JSON.stringify({ name: teamName }),
      });
      setTeamSuccess('Team name updated.');
    } catch (err) { setTeamError(err.message); }
    finally { setTeamSaving(false); }
  };

  // Password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(''); setPasswordSuccess('');
    if (newPassword.length < 12) {
      setPasswordError('New password must be at least 12 characters.');
      return;
    }
    if (!/[a-zA-Z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one letter.');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must contain at least one number.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await apiFetch('/api/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      setPasswordSuccess('Password changed.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { setPasswordError(err.message); }
    finally { setPasswordSaving(false); }
  };

  return (
    <div className="admin-layout">
      {user?.role === 'super_admin' ? (
        <SuperAdminNav activeItem="account" />
      ) : (
        <header className="header">
          <div className="header-left">
            <img src={LOGO} alt="Train Daily" className="header-logo" />
            <div className="header-title">Account Settings</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-sm btn-outline" onClick={() => navigate(backPath)}>Back</button>
            <button className="header-logout" onClick={logout}>Log out</button>
          </div>
        </header>
      )}

      <div style={{ padding: '16px 16px 80px', maxWidth: 600, margin: '0 auto' }}>

        {/* Section 1: Your Info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Your Info</h3>
          <form onSubmit={handleSaveProfile}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>First Name</label>
              <input type="text" className="input" style={{ padding: '10px 12px' }} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Last Name</label>
              <input type="text" className="input" style={{ padding: '10px 12px' }} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Email</label>
              <input type="email" className="input" style={{ padding: '10px 12px' }} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            {profileError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{profileError}</div>}
            {profileSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{profileSuccess}</div>}
            <button type="submit" className="btn btn-orange" disabled={profileSaving} style={{ width: '60%' }}>
              {profileSaving ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>

        {/* Section 2: Club Name (club_admin only) */}
        {user?.role === 'club_admin' && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Club Name</h3>
            {clubLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading...</div>
            ) : (
              <form onSubmit={handleSaveClubName}>
                <div style={{ marginBottom: 12 }}>
                  <input type="text" className="input" style={{ padding: '10px 12px' }} value={clubName} onChange={(e) => setClubName(e.target.value)} required />
                </div>
                {clubError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{clubError}</div>}
                {clubSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{clubSuccess}</div>}
                <button type="submit" className="btn btn-orange" disabled={clubSaving} style={{ width: '60%' }}>
                  {clubSaving ? 'Saving...' : 'Save Club Name'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Section 3: Team Name */}
        {teams && teams.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Team Name</h3>
            <form onSubmit={handleSaveTeamName}>
              {teams.length > 1 && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Select Team</label>
                  <select className="input" style={{ padding: '10px 12px' }} value={selectedTeamId} onChange={(e) => setSelectedTeamId(e.target.value)}>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ marginBottom: 12 }}>
                {teams.length === 1 && (
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Team Name</label>
                )}
                <input type="text" className="input" style={{ padding: '10px 12px' }} value={teamName} onChange={(e) => setTeamName(e.target.value)} required />
              </div>
              {teamError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{teamError}</div>}
              {teamSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{teamSuccess}</div>}
              <button type="submit" className="btn btn-orange" disabled={teamSaving || !selectedTeamId} style={{ width: '60%' }}>
                {teamSaving ? 'Saving...' : 'Save Team Name'}
              </button>
            </form>
          </div>
        )}

        {/* Section 4: Change Password */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Change Password</h3>
          <form onSubmit={handleChangePassword}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Current Password</label>
              <input type="password" className="input" style={{ padding: '10px 12px' }} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>New Password</label>
              <input type="password" className="input" style={{ padding: '10px 12px' }} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={12} />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>At least 12 characters, must include a letter and a number</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Confirm New Password</label>
              <input type="password" className="input" style={{ padding: '10px 12px' }} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
            {passwordError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{passwordError}</div>}
            {passwordSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{passwordSuccess}</div>}
            <button type="submit" className="btn btn-orange" disabled={passwordSaving} style={{ width: '60%' }}>
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
